/**
 * <product-gallery> — Web Component para galeria de mídia do produto.
 * Substitui Slick carousel por navegação vanilla com scroll-snap mobile.
 *
 * Funcionalidades:
 * - Thumbnail click → troca slide ativo
 * - Setas prev/next
 * - Touch swipe (pointer events) no mobile
 * - Contador mobile (1/N)
 * - Pausa vídeo ao trocar slide
 * - Compatível com Fancybox (data-fancybox) e zoom (data-zoom-image)
 * - Compatível com variants.js via data-media-id click
 */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 1;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.isDragging = false;
    this.swipeThreshold = 40;
  }

  connectedCallback() {
    this.mainContainer = this.querySelector('[data-gallery-main]');
    this.thumbsContainer = this.querySelector('[data-gallery-thumbs]');
    this.dotsContainer = this.querySelector('[data-gallery-dots]');
    this.slides = Array.from(this.querySelectorAll('.product-gallery__slide'));
    this.thumbs = this.thumbsContainer
      ? Array.from(this.thumbsContainer.querySelectorAll('.product-gallery__thumb'))
      : [];
    this.dots = this.dotsContainer
      ? Array.from(this.dotsContainer.querySelectorAll('.product-gallery__dot'))
      : [];
    this.prevBtn = this.querySelector('[data-action="prev"]');
    this.nextBtn = this.querySelector('[data-action="next"]');
    this.counterCurrent = this.querySelector('[data-current]');
    this.counterTotal = this.querySelector('[data-total]');
    this.mediaCount = this.slides.length;
    this.isGrid = this.dataset.layout === 'grid';

    if (this.mediaCount === 0) return;

    this.bindEvents();
    this.updateArrows();
    this.initZoom();
    this.initFancybox();

    if (this.isGrid) {
      this.initGridMobileObserver();
      this.initGridScrollLimit();
    }
  }

  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._gridObserver) {
      this._gridObserver.disconnect();
    }
  }

  bindEvents() {
    this._abortController = new AbortController();
    const opts = { signal: this._abortController.signal };

    // Thumbnail clicks
    if (this.thumbsContainer) {
      this.thumbsContainer.addEventListener('click', (e) => {
        const thumb = e.target.closest('.product-gallery__thumb');
        if (!thumb) return;
        const index = parseInt(thumb.dataset.index, 10);
        if (index) this.goTo(index);
      }, opts);
    }

    // Arrow clicks
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev(), opts);
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next(), opts);
    }

    // Dot clicks
    if (this.dotsContainer) {
      this.dotsContainer.addEventListener('click', (e) => {
        const dot = e.target.closest('.product-gallery__dot');
        if (!dot) return;
        const index = parseInt(dot.dataset.index, 10);
        if (index) this.goTo(index);
      }, opts);
    }

    // Touch swipe on main container
    if (this.mainContainer) {
      this.mainContainer.addEventListener('pointerdown', (e) => this.onPointerDown(e), opts);
      this.mainContainer.addEventListener('pointermove', (e) => this.onPointerMove(e), opts);
      this.mainContainer.addEventListener('pointerup', (e) => this.onPointerUp(e), opts);
      this.mainContainer.addEventListener('pointercancel', () => this.onPointerCancel(), opts);
    }

    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    }, opts);

    // Listen for clicks on [data-media-id] from variants.js
    // variants.js does: element.click() on thumbnail with matching data-media-id
    // Our thumbsContainer click handler above already handles this.
  }

  goTo(index) {
    if (index < 1 || index > this.mediaCount || index === this.currentIndex) return;

    // Pause any playing video on current slide
    this.pauseMedia(this.slides[this.currentIndex - 1]);

    // In grid mode on desktop, scroll to the target slide if scrollable
    if (this.isGrid && window.innerWidth >= 750) {
      const targetSlide = this.slides[index - 1];
      if (targetSlide && this.mainContainer) {
        targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      this.currentIndex = index;
      this.updateDots(index);
      return;
    }

    // Grid mobile: scroll to slide instead of toggling visibility
    if (this.isGrid) {
      const targetSlide = this.slides[index - 1];
      if (targetSlide && this.mainContainer) {
        targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      this.currentIndex = index;
      this.updateDots(index);
      this.updateCounter();
      return;
    }

    // Update slides
    this.slides.forEach((slide) => {
      slide.classList.toggle('is-active', parseInt(slide.dataset.index, 10) === index);
    });

    // Update thumbs
    this.thumbs.forEach((thumb) => {
      const isActive = parseInt(thumb.dataset.index, 10) === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    this.updateDots(index);

    // Scroll active thumb into view
    const activeThumb = this.thumbs.find(
      (t) => parseInt(t.dataset.index, 10) === index
    );
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }

    this.currentIndex = index;
    this.updateArrows();
    this.updateCounter();
  }

  updateDots(index) {
    this.dots.forEach((dot) => {
      const isActive = parseInt(dot.dataset.index, 10) === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  /**
   * Navigate to media by its Shopify media ID.
   * Called externally by variants.js compatibility layer.
   */
  goToMediaId(mediaId) {
    const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
    if (slide) {
      this.goTo(parseInt(slide.dataset.index, 10));
    }
  }

  /**
   * Initialize IntersectionObserver for mobile scroll-snap grid mode.
   * Updates dots based on which slide is most visible.
   */
  initGridMobileObserver() {
    if (window.innerWidth >= 750 || !this.mainContainer) return;

    this._gridObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const index = parseInt(entry.target.dataset.index, 10);
            if (index && index !== this.currentIndex) {
              this.currentIndex = index;
              this.updateDots(index);
              this.updateCounter();
            }
          }
        }
      },
      { root: this.mainContainer, threshold: 0.5 }
    );

    this.slides.forEach((slide) => this._gridObserver.observe(slide));
  }

  /**
   * Grid desktop: measure actual rendered row heights and cap to 2 rows.
   * Also handles wheel passthrough so page scrolls when gallery reaches limits.
   */
  initGridScrollLimit() {
    if (window.innerWidth < 750 || !this.mainContainer || this.slides.length <= 4) return;

    const applyLimit = () => {
      const gap = 0.4 * parseFloat(getComputedStyle(document.documentElement).fontSize || 16);
      // Measure height of first two rows (slides 0,1 = row 1; slides 2,3 = row 2)
      const row1 = this.slides[0] ? this.slides[0].offsetHeight : 0;
      const row2 = this.slides[2] ? this.slides[2].offsetHeight : 0;
      if (row1 > 0 && row2 > 0) {
        this.mainContainer.style.maxHeight = (row1 + row2 + gap) + 'px';
      }
    };

    // Wait for first image to load, then measure
    const firstImg = this.slides[0] && this.slides[0].querySelector('img');
    if (firstImg && firstImg.complete) {
      applyLimit();
    } else if (firstImg) {
      firstImg.addEventListener('load', applyLimit, { once: true });
    }

    // Native overflow-y: auto handles scrolling.
    // No wheel handler needed — browser scrolls gallery when inside,
    // and page when gallery reaches boundary.
  }

  prev() {
    if (this.currentIndex > 1) {
      this.goTo(this.currentIndex - 1);
    }
  }

  next() {
    if (this.currentIndex < this.mediaCount) {
      this.goTo(this.currentIndex + 1);
    }
  }

  updateArrows() {
    if (this.isGrid) return;
    if (this.prevBtn) {
      this.prevBtn.hidden = this.currentIndex <= 1;
    }
    if (this.nextBtn) {
      this.nextBtn.hidden = this.currentIndex >= this.mediaCount;
    }
  }

  updateCounter() {
    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.currentIndex;
    }
  }

  // ---- Touch Swipe ----

  onPointerDown(e) {
    if (e.pointerType === 'mouse') return; // Only swipe on touch
    if (this.isGrid) return; // Scroll-snap handles grid mobile
    this.isDragging = true;
    this.pointerStartX = e.clientX;
    this.pointerStartY = e.clientY;
    this.mainContainer.setPointerCapture(e.pointerId);
  }

  onPointerMove(e) {
    if (!this.isDragging) return;
    // Prevent vertical scroll while swiping horizontally
    const dx = Math.abs(e.clientX - this.pointerStartX);
    const dy = Math.abs(e.clientY - this.pointerStartY);
    if (dx > dy && dx > 10) {
      e.preventDefault();
    }
  }

  onPointerUp(e) {
    if (!this.isDragging) return;
    this.isDragging = false;
    const dx = e.clientX - this.pointerStartX;
    if (Math.abs(dx) > this.swipeThreshold) {
      if (dx < 0) this.next();
      else this.prev();
    }
  }

  onPointerCancel() {
    this.isDragging = false;
  }

  // ---- Video Control ----

  pauseMedia(slide) {
    if (!slide) return;
    const video = slide.querySelector('video');
    if (video) video.pause();

    const iframe = slide.querySelector('iframe');
    if (iframe) {
      // Pause YouTube/Vimeo via postMessage
      const src = iframe.src || '';
      if (src.includes('youtube')) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), '*');
      } else if (src.includes('vimeo')) {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    }
  }

  // ---- Zoom ----

  initZoom() {
    if (window.innerWidth <= 1024) return;
    if (this.isGrid) return;
    const zoomElements = this.querySelectorAll('[data-zoom-image]');
    if (zoomElements.length === 0) return;

    // Use jQuery zoom if available (legacy compat)
    if (typeof jQuery !== 'undefined' && jQuery.fn.zoom) {
      zoomElements.forEach((el) => {
        jQuery(el).zoom({ url: el.dataset.zoomImage, touch: false });
      });
    }
  }

  // ---- Fancybox ----

  initFancybox() {
    const fancyboxEls = this.querySelectorAll('[data-fancybox]');
    if (fancyboxEls.length === 0) return;

    // Use jQuery Fancybox if available (legacy compat)
    if (typeof jQuery !== 'undefined' && jQuery.fn.fancybox) {
      jQuery(fancyboxEls).fancybox({
        buttons: ['zoom', 'slideShow', 'fullScreen', 'thumbs', 'close'],
        loop: true,
        thumbs: { autoStart: true },
      });
    }
  }
}

customElements.define('product-gallery', ProductGallery);
